
import React, { useEffect, useState } from "react";
import { Gara } from "../../types/gare/gara";
import { ConcorrentiService } from "../../services/concorrentiService";
import { GaraService } from "../../services/garaService";
import { Concorrente } from "../../types/concorrenti/concorrente";

interface Props {
  gara: Gara;
  onOpenResults?: () => void;
  pendingCompetitors?: Concorrente[];
  onStageCompetitor?: (competitor: Concorrente) => void;
  onCancelCompetitor?: (id: number) => void;
}

const PartecipantiIndividualiTab: React.FC<Props> = ({ gara, onOpenResults, pendingCompetitors, onStageCompetitor, onCancelCompetitor }) => {
  const [selectedConcorrente, setSelectedConcorrente] = useState<string>("");
  const [concorrentiDisponibili, setConcorrentiDisponibili] = useState<Concorrente[]>([]);
  const searchTerm = "";
  const [concorrentiIscritti, setConcorrentiIscritti] = useState<Concorrente[]>(gara.competitors || []);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [published, setPublished] = useState(Boolean(gara.resultsPublished));
  const [busy, setBusy] = useState(false);
  const iscrittiVisibili = [...concorrentiIscritti, ...(pendingCompetitors || []).filter(c => !concorrentiIscritti.some(existing => existing.id === c.id))];

  useEffect(() => {
    let active = true;
    setBusy(true);
    GaraService.getGaraById(gara.id).then(fresh => {
      if (!active) return;
      setConcorrentiIscritti(fresh.competitors || []);
      setPublished(Boolean(fresh.resultsPublished));
    }).catch(e => { if (active) setRemoveError(e.message || "Impossibile caricare gli iscritti."); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [gara.id]);

  useEffect(() => {
    const loadConcorrentiDisponibili = async () => {
      try {
        const data = await ConcorrentiService.getConcorrentiList(1, 1000, searchTerm, undefined, undefined, gara?.discipline || 1);
        const assegnatiIds = [...concorrentiIscritti, ...(pendingCompetitors || [])].map(c => c.id);
        const disponibili = data.results.filter(
          (c) => !assegnatiIds.includes(c.id)
        );
        setConcorrentiDisponibili(disponibili);
      } catch (err) {
        console.error("Error loading concorrenti disponibili:", err);
      }
    };
    loadConcorrentiDisponibili();
  }, [searchTerm, concorrentiIscritti, gara.discipline, pendingCompetitors]);

  const handleAddConcorrente = (concorrenteId: string) => {
    const id = parseInt(concorrenteId);
    const concorrenteDaAggiungere = concorrentiDisponibili.find((c) => c.id === id);
    if (concorrenteDaAggiungere && gara) {
      if (gara.discipline === 2 && published) {
        onStageCompetitor?.(concorrenteDaAggiungere);
        setSelectedConcorrente("");
        return;
      }
      setBusy(true);
      setSelectedConcorrente("");
      GaraService.addConcorrente(gara.id, concorrenteDaAggiungere.id)
        .then(() => {
          setConcorrentiIscritti((prev) => [...prev, concorrenteDaAggiungere]);
        })
        .catch((error) => {
          setRemoveError(error.message || "Il concorrente non può essere iscritto.");
          console.error("Errore nell'aggiunta del concorrente:", error);
        }).finally(() => setBusy(false));
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Concorrenti Individuali</h2>
      {gara.discipline === 2 && published && <div className="mb-4 text-sm text-gray-600">
        <p>Aggiungi qui i concorrenti. Poi inserisci i loro pesi in Classifica e salva per aggiornare la gara pubblicata.</p>
        <button onClick={onOpenResults} className="text-brand-700 underline mt-2">Apri classifica</button>
      </div>}
      <div className="mb-4">
        {/* <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Cerca concorrente..."
          className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        /> */}
        <select
          aria-label="Aggiungi concorrente alla gara"
          value={selectedConcorrente}
          disabled={busy}
          onChange={(e) => handleAddConcorrente(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">
            Seleziona un concorrente disponibile...
          </option>
          {concorrentiDisponibili.map((concorrente) => (
            <option key={concorrente.id} value={concorrente.id}>
              {concorrente.lastName} {concorrente.firstName}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        {iscrittiVisibili.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            Nessun concorrente iscritto
          </p>
        ) : (
          iscrittiVisibili.map((concorrente) => (
            <div
              key={concorrente.id}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-4">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {concorrente.lastName} {concorrente.firstName}
                    </h4>
                    {pendingCompetitors?.some(c => c.id === concorrente.id) && <p className="mt-1 text-xs text-brand-700">Da completare e salvare in Classifica</p>}
                  </div>
                </div>
              </div>
              <button
                disabled={busy}
                onClick={async () => {
                  if (pendingCompetitors?.some(c => c.id === concorrente.id)) {
                    onCancelCompetitor?.(concorrente.id);
                    return;
                  }
                  setBusy(true);
                  try {
                    await GaraService.removeConcorrente(gara.id, concorrente.id);
                    setConcorrentiIscritti(prev => prev.filter(c => c.id !== concorrente.id));
                    setConcorrentiDisponibili(prev => [...prev, concorrente]);
                  } catch (err: any) {
                    if (err?.status === 409) {
                      setRemoveError("Impossibile rimuovere il concorrente: esistono dati collegati (turni, settori o classifica). Rimuovi prima i dati associati.");
                    } else {
                      setRemoveError("Errore nella rimozione del concorrente.");
                    }
                    console.error("Errore nella rimozione del concorrente:", err);
                  } finally { setBusy(false); }
                }}
                className="text-red-500 hover:text-red-700 font-medium text-sm transition-colors"
              >
                Rimuovi
              </button>
            </div>
          ))
        )}
      </div>

      {/* Popup errore rimozione concorrente */}
      {removeError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setRemoveError(null)} />
          <div className="relative bg-white rounded-lg border border-brand-200 p-6 max-w-sm mx-auto">
            <h3 className="text-lg font-bold mb-2 text-red-600">Errore</h3>
            <p className="mb-4 text-gray-700">{removeError}</p>
            <button
              className="bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded"
              onClick={() => setRemoveError(null)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartecipantiIndividualiTab;
